import { Autocomplete, Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import { DownloadFileDto, MediaInfoDto, MediaPlacementType } from '@sr/dto';
import { Field, FieldProps, FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { TextFieldProps, fieldToTextField } from 'formik-mui';
import { useEffect, useMemo, useRef } from 'react';
import { CityPicker } from 'entities/geo/cities/city-picker';
import { MediaTypePicker } from 'entities/media/media-type-picker';
import { useMediaList } from 'entities/media/media-list';
import { useAddMediaPrice } from 'entities/media/media-price';
import { MediaCreateDialog, useAddMediaDialog, useAddNewMedia } from 'features/media/create';
import FilePickerField from 'shared/ui/formik-fields/file-picker';
import { useSnack } from 'shared/ui/snack';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';

export type CompanyMediaAttachFormValues = {
  cityId: number | '';
  mediaTypeId: number | '';
  mediaId: number | '';
  mainFile: DownloadFileDto[];
  extraFiles: DownloadFileDto[];
};

export const companyMediaAttachEmptyValues: CompanyMediaAttachFormValues = {
  cityId: '',
  mediaTypeId: '',
  mediaId: '',
  mainFile: [],
  extraFiles: [],
};

export const validateCompanyMediaAttach = (values: CompanyMediaAttachFormValues) => {
  const errors: Partial<Record<keyof CompanyMediaAttachFormValues, string>> = {};

  if (!values.cityId) errors.cityId = 'Обязательное поле';
  if (!values.mediaTypeId) errors.mediaTypeId = 'Обязательное поле';
  if (!values.mediaId) errors.mediaId = 'Обязательное поле';
  if (!values.mainFile || values.mainFile.length === 0) errors.mainFile = 'Обязательное поле';

  return errors;
};

const MediaTypePickerField = (props: FieldProps<CompanyMediaAttachFormValues['mediaTypeId'], CompanyMediaAttachFormValues> & { label?: string }) => {
  const { field, form, label = 'Тип СМИ' } = props;
  const { setFieldValue, errors, touched } = form;
  const { name, value } = field;
  const key = name as keyof CompanyMediaAttachFormValues;
  const error = (errors as FormikErrors<CompanyMediaAttachFormValues>)[key];
  const isTouched = (touched as FormikTouched<CompanyMediaAttachFormValues>)[key];
  const errorText = isTouched ? (typeof error === 'string' ? error : undefined) : undefined;

  return (
    <MediaTypePicker
      value={value}
      onChange={(v: number | string) => setFieldValue(name, v)}
      label={label}
      size="small"
      error={isTouched && error !== undefined}
      helperText={errorText}
    />
  );
};

const MediaPickerField = (props: TextFieldProps & { options: MediaInfoDto[]; disabled?: boolean }) => {
  const {
    form: { setFieldValue },
    field: { name, value },
    label,
    options,
    disabled,
  } = props;

  const selected = options.find((m) => m.id === value) ?? null;

  return (
    <Autocomplete
      value={selected}
      autoComplete={false}
      options={options}
      onChange={(_e, v) => setFieldValue(name, v ? v.id : '')}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(o) => o.name}
      loadingText={'Загрузка...'}
      noOptionsText={'Ничего не найдено'}
      disabled={disabled}
      renderInput={(params) => (
        <TextField {...fieldToTextField(props)} {...params} name={name} label={label} placeholder="Начните набирать..." />
      )}
    />
  );
};

export const CompanyMediaAttachForm = ({ companyId, onClose }: { companyId: number; onClose: () => void }) => {
  const { values, setFieldValue, touched, errors } = useFormikContext<CompanyMediaAttachFormValues>();
  const { openDialog, closeDialog } = useAddMediaDialog();
  const addNewMedia = useAddNewMedia();
  const addMediaPrice = useAddMediaPrice();
  const { showError, showSuccess } = useSnack();
  const prevCityId = useRef<CompanyMediaAttachFormValues['cityId']>(values.cityId);
  const prevMediaTypeId = useRef<CompanyMediaAttachFormValues['mediaTypeId']>(values.mediaTypeId);

  useEffect(() => {
    if (prevCityId.current !== values.cityId || prevMediaTypeId.current !== values.mediaTypeId) {
      setFieldValue('mediaId', '');
    }
    prevCityId.current = values.cityId;
    prevMediaTypeId.current = values.mediaTypeId;
  }, [setFieldValue, values.cityId, values.mediaTypeId]);

  const filterParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('placement', MediaPlacementType.LOCAL);
    if (values.cityId) params.set('cityId', String(values.cityId));
    if (values.mediaTypeId) params.set('mediaTypeId', String(values.mediaTypeId));
    return params;
  }, [values.cityId, values.mediaTypeId]);

  const mediaCollection = useMediaList({ page: 0, itemsPerPage: 50 }, filterParams);
  const mediaOptions = mediaCollection.collection.items;

  const canCreateMedia = Boolean(values.cityId && values.mediaTypeId);

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Typography variant="subtitle2">Выберите город и тип СМИ</Typography>

      <Stack spacing={2}>
        <Field name="cityId" label="Город" component={CityPicker} fullWidth />
        <Field name="mediaTypeId" component={MediaTypePickerField} />
      </Stack>

      <Field
        name="mediaId"
        label="СМИ"
        component={MediaPickerField}
        options={mediaOptions}
        disabled={!values.cityId || !values.mediaTypeId}
        helperText={touched.mediaId && errors.mediaId ? errors.mediaId : mediaCollection.error ? String(mediaCollection.error) : undefined}
      />

      <Box>
        <Button variant="outlined" onClick={() => openDialog()} disabled={!canCreateMedia}>
          Создать СМИ
        </Button>
      </Box>

      <Divider />

      <Typography variant="subtitle2">Загрузите прайс поставщика</Typography>

      <Box>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Основной файл
        </Typography>
        <Field name="mainFile" component={FilePickerField} fileCategory="media-price" />
        {touched.mainFile && typeof errors.mainFile === 'string' ? (
          <Typography variant="caption" color="error">
            {errors.mainFile as string}
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ display: 'block', height: 18 }}>
            {' '}
          </Typography>
        )}
      </Box>

      <Box>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Доп. файлы
        </Typography>
        <Field name="extraFiles" component={FilePickerField} fileCategory="media-price" multiple />
      </Box>

      <MediaCreateDialog
        initialValues={{
          placement: MediaPlacementType.LOCAL,
          cityId: values.cityId ?? '',
          mediaTypeId: values.mediaTypeId ?? '',
        }}
        onSubmit={async (data) => {
          try {
            const created = await addNewMedia(data);
            closeDialog();

            if (!created?.id) throw new Error('Не удалось создать СМИ');

            setFieldValue('mediaId', created.id);

            if (values.mainFile.length > 0) {
              await addMediaPrice({
                media: created.id,
                companies: String(companyId),
                mainFile: values.mainFile[0],
                extraFiles: values.extraFiles,
              });
              showSuccess('СМИ успешно создано и прикреплено к поставщику');
              onClose();
            } else {
              showSuccess('СМИ успешно создано');
            }
          } catch (e: unknown) {
            showError(extractNestJsErrorMessage(e) || 'Ошибка создания СМИ');
          }
        }}
      />
    </Stack>
  );
};
